import { IDetailsRowProps, ILinkStyles, MessageBarType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  BaseDialog,
  CommonStyles,
  debounce,
  defaultPalette,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesBasic,
  detailsListStylesCard,
  EmptyMessage,
  getItemsWithEmptyItemsOfNextPage,
  highlightRowBorder,
  IColumnWithDataType,
  IDetailsListProps,
  Item,
  itemStylesTruncatedTag,
  Link,
  linkStylesGhost,
  linkStylesPrimary,
  ListRow,
  listRowStylesThin,
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBar,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { fsApi } from '../../featurestore/api';
import { useHasReviewerRole, useRefetchPendingReviews } from '../../featurestore/featurestore.hooks';
import {
  CoreService,
  FeatureSetResponse,
  FeatureSetReview,
  FeatureSetToReview,
  ReviewStatus,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { CommonImageSrc, ContentsTitle, defaultPageSize } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import {
  IPendingReviewListClassNames,
  IPendingReviewListStyles,
  pendingReviewListStylesDefault,
} from './PendingReviewList.styles';

enum RequestAction {
  Approve = 'Approve',
  Reject = 'Reject',
  Delete = 'Delete',
}

const statusColorMap = new Map<ReviewStatus | undefined, string>([
  [ReviewStatus.REVIEW_STATUS_APPROVED, defaultPalette.green100],
  [ReviewStatus.REVIEW_STATUS_UNSPECIFIED, defaultPalette.yellow100],
  [ReviewStatus.REVIEW_STATUS_CREATED, defaultPalette.yellow100],
  [ReviewStatus.REVIEW_STATUS_REJECTED, defaultPalette.red100],
  [ReviewStatus.REVIEW_STATUS_TO_REVIEW, defaultPalette.blue100],
]);

interface DialogInfo {
  messageBarType: MessageBarType;
  message: (item?: FeatureSetReviewInfo) => React.ReactNode;
  linkStyles: Partial<ILinkStyles>;
}

const dialogActionMap = new Map<RequestAction | undefined, DialogInfo>([
  [
    RequestAction.Reject,
    {
      messageBarType: MessageBarType.error,
      message: (item) => (
        <div>
          Are you sure you want to reject the feature set <strong>{item?.featureSetName}</strong>?
        </div>
      ),
      linkStyles: {
        root: {
          borderColor: defaultPalette.red500,
          backgroundColor: defaultPalette.red500,
          color: defaultPalette.white,
        },
      },
    },
  ],
  [
    RequestAction.Approve,
    {
      messageBarType: MessageBarType.info,
      message: (item) => (
        <div>
          Do you want to approve <strong>{item?.featureSetName}</strong>?
        </div>
      ),
      linkStyles: {
        root: {
          borderColor: defaultPalette.green500,
          backgroundColor: defaultPalette.green500,
          color: defaultPalette.white,
        },
      },
    },
  ],
  [
    RequestAction.Delete,
    {
      messageBarType: MessageBarType.error,
      message: (item) => (
        <div>
          Are you sure you want to delete the feature set <strong>{item?.featureSetName}</strong>?
        </div>
      ),
      linkStyles: {
        root: {
          borderColor: defaultPalette.red500,
          backgroundColor: defaultPalette.red500,
          color: defaultPalette.white,
        },
      },
    },
  ],
]);

const getColumns = (
  title: ContentsTitle,
  onAction: (action: RequestAction, item: FeatureSetReviewInfo) => void,
  onView: (item: FeatureSetReviewInfo) => void,
  isCardView: boolean,
  hasReviewerRole?: boolean
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'featureSetName',
      name: 'Feature Set Name',
      fieldName: 'featureSetName',
      minWidth: 150,
      data: {
        listCellProps: isCardView
          ? {
              onRenderPrefix: (item) =>
                item.featureSetName?.toLowerCase().includes('h2o') ? (
                  <ListCellImage imageSrc={CommonImageSrc.logo512} />
                ) : (
                  <ListCellImage iconName="DocumentSet" />
                ),
            }
          : undefined,
      },
    },
    {
      key: 'featureSetMajorVersion',
      name: 'Major Version',
      fieldName: 'featureSetMajorVersion',
      minWidth: 120,
    },
    {
      key: 'projectName',
      name: 'Project Name',
      fieldName: 'projectName',
      minWidth: 150,
    },
    {
      key: 'statusName',
      name: 'Review Status',
      fieldName: 'statusName',
      minWidth: 120,
      data: {
        listCellProps: {
          onRenderText: (item: FeatureSetReviewInfo) => (
            <Item
              styles={itemStylesTruncatedTag}
              idField="statusName"
              styleField="style"
              labelField="statusName"
              titleField="statusName"
              data={{ ...item, style: { backgroundColor: statusColorMap.get(item.status) } }}
            />
          ),
        },
      },
    },
    {
      key: 'reviewerName',
      name: title === ContentsTitle.pendingReviews ? 'Author' : 'Reviewer',
      fieldName: 'reviewerName',
      minWidth: 100,
    },
  ];

  if (title === ContentsTitle.pendingReviews) {
    columns.push(
      {
        key: 'createdAt',
        name: 'Created At',
        fieldName: 'createdAt',
        minWidth: 180,
        data: {
          listCellProps: {
            onRenderText: (item: FeatureSetReviewInfo) => <>{formatDefaultDateAndTimeWithSeconds(item.createdAt)}</>,
          },
        },
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 340,
        onRender: (item: FeatureSetReviewInfo) =>
          hasReviewerRole ? (
            <div
              style={{
                ...(CommonStyles.flexCenterVertically as CSSProperties),
                width: '100%',
              }}
            >
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                {item.status === ReviewStatus.REVIEW_STATUS_TO_REVIEW ? (
                  <>
                    <Link
                      styles={[
                        linkStylesGhost,
                        { root: { borderColor: defaultPalette.green900, color: defaultPalette.green900 } },
                      ]}
                      iconName="SkypeCircleCheck"
                      onClick={() => onAction(RequestAction.Approve, item)}
                    >
                      {RequestAction.Approve}
                    </Link>
                    <Link
                      styles={[
                        linkStylesGhost,
                        { root: { borderColor: defaultPalette.red900, color: defaultPalette.red900 } },
                      ]}
                      iconName="StatusErrorFull"
                      onClick={() => onAction(RequestAction.Reject, item)}
                    >
                      {RequestAction.Reject}
                    </Link>
                  </>
                ) : null}
                <Link styles={linkStylesGhost} onClick={() => onView(item)}>
                  View
                </Link>
              </div>
            </div>
          ) : (
            <></>
          ),
      }
    );
  } else {
    columns.push(
      {
        key: 'reviewedAt',
        name: 'Reviewed At',
        fieldName: 'reviewedAt',
        minWidth: 180,
        data: {
          listCellProps: {
            onRenderText: (item: FeatureSetReviewInfo) => <>{formatDefaultDateAndTimeWithSeconds(item.reviewedAt)}</>,
          },
        },
      },
      {
        key: 'reason',
        name: 'Reason',
        fieldName: 'reason',
        minWidth: 150,
        data: {
          listCellProps: { emptyMessage: '-' },
        },
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 200,
        onRender: (item: FeatureSetReviewInfo) => (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              textAlign: 'right',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {item.status === ReviewStatus.REVIEW_STATUS_TO_REVIEW ||
              item.status === ReviewStatus.REVIEW_STATUS_REJECTED ? (
                <Link styles={[linkStylesGhost]} onClick={() => onAction(RequestAction.Delete, item)}>
                  {RequestAction.Delete}
                </Link>
              ) : null}
              <Link styles={linkStylesGhost} onClick={() => onView(item)}>
                View
              </Link>
            </div>
          </div>
        ),
      }
    );
  }

  return columns;
};

const reviewStatusNameMap = new Map<ReviewStatus | undefined, string>([
  [ReviewStatus.REVIEW_STATUS_APPROVED, 'Approved'],
  [ReviewStatus.REVIEW_STATUS_REJECTED, 'Rejected'],
  [ReviewStatus.REVIEW_STATUS_TO_REVIEW, 'To Review'],
  [ReviewStatus.REVIEW_STATUS_CREATED, 'Created'],
  [ReviewStatus.REVIEW_STATUS_UNSPECIFIED, 'Unspecified'],
]);

const getListItem = (item: FeatureSetToReview & FeatureSetReview): FeatureSetReviewInfo => {
  const reviewerName = item.reviewer?.name || item.owner?.name;
  return {
    ...item,
    statusName: reviewStatusNameMap.get(item.status),
    reviewerName,
  };
};

const getAPI = (title: ContentsTitle) => {
  return title === ContentsTitle.pendingReviews
    ? CoreService.ListFeatureSetsToReview
    : CoreService.ListFeatureSetsReviewsPage;
};

interface FeatureSetReviewInfo extends FeatureSetReview {
  reviewerName?: string;
  statusName?: string;
}

interface PendingReviewListData {
  items: FeatureSetReviewInfo[];
  pageToken?: string;
  pageSize?: number;
}

interface IPendingReviewListProps {
  title?: ContentsTitle;
  isCardView?: boolean;
  filters?: ReviewStatus[];
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
}

export const PendingReviewList = ({
  title = ContentsTitle.pendingReviews,
  isCardView = true,
  filters,
  onUpdateData,
}: IPendingReviewListProps) => {
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [data, setData] = useState<PendingReviewListData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const hasReviewerRole = useHasReviewerRole();
  const refetchPendingReviews = useRefetchPendingReviews();
  const refMeta = useRef<{
    isLoadingPage?: boolean;
    actionType?: RequestAction;
    accessRequest?: FeatureSetReviewInfo;
    dialogInfo?: DialogInfo;
    reason?: string;
  }>({ isLoadingPage: false, reason: '' });
  const { addToast } = useToast();
  const history = useHistory();
  const load = useCallback(
    async ({ items, pageToken }: PendingReviewListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading ${title}...`);
      try {
        const pageSize = defaultPageSize;
        const { entries = [], nextPageToken } = await fsApi(getAPI(title), {
          filters,
          pageSize,
          pageToken,
        });
        const newItems = getItemsWithEmptyItemsOfNextPage(items, entries, pageSize, getListItem);
        const newData = {
          items: newItems,
          pageToken: nextPageToken,
          pageSize,
        };
        setData({
          ...newData,
        });
        onUpdateData && onUpdateData(newData.items.length, newData.pageSize, newData.pageToken, ContentsTitle.reviews);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading ${title}: `));
      } finally {
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, filters, title]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const doAction = useCallback(async () => {
    const actionType = refMeta.current.actionType;
    const reason = refMeta.current.reason;
    setLoadingMessage(`Apply ${actionType} request...`);
    try {
      const { reviewId, featureSetName } = refMeta.current.accessRequest || {};
      let message = '';
      switch (actionType) {
        case RequestAction.Approve:
          await fsApi(CoreService.ApproveReview, {
            reviewId,
            reason,
          });
          message = `You have approved the feature set '${featureSetName}'`;
          addToast({
            messageBarType: MessageBarType.success,
            message,
          });
          refetchPendingReviews();
          break;
        case RequestAction.Reject:
          await fsApi(CoreService.RejectReview, {
            reviewId,
            reason,
          });
          message = `You have rejected the feature set '${featureSetName}'`;
          addToast({
            messageBarType: MessageBarType.success,
            message,
          });
          refetchPendingReviews();
          break;
        case RequestAction.Delete:
          await fsApi(CoreService.DeleteFeatureSetVersionInReview, {
            reviewId,
          });
          message = `You have deleted feature set in review '${featureSetName}'`;
          addToast({
            messageBarType: MessageBarType.success,
            message,
          });
          break;
      }
      load();
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while applying ${actionType} request: `));
    } finally {
      setLoadingMessage('');
    }
  }, [addToast, load]);
  const onAction = useCallback(
    (action: RequestAction, item: FeatureSetReviewInfo) => {
      refMeta.current = {
        ...refMeta.current,
        accessRequest: item,
        actionType: action,
        dialogInfo: dialogActionMap.get(action),
        reason: '',
      };
      toggleHideDialog();
    },
    [toggleHideDialog, doAction]
  );
  const onView = useCallback(
    async ({ featureSetId, featureSetMajorVersion, status, reviewId }: FeatureSetReviewInfo) => {
      if (status === ReviewStatus.REVIEW_STATUS_APPROVED || status === ReviewStatus.REVIEW_STATUS_CREATED) {
        setLoadingMessage(`loading Feature Set`);
        try {
          const featureSetVersion = `${featureSetMajorVersion}.0`;
          const { featureSet }: FeatureSetResponse = await fsApi(CoreService.GetFeatureSetsLastMinorForCurrentMajor, {
            featureSetId,
            featureSetVersion,
          });
          setLoadingMessage('');
          history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${featureSet?.id}/version/${featureSet?.version}`);
        } catch (err) {
          addToast(getErrorMessageBarMessage(err, `An error has occurred while loading feature set: `));
          setLoadingMessage('');
        }
      } else {
        if (title === ContentsTitle.pendingReviews) {
          history.push(`${RoutePaths.FEATURESTORE_PENDING_REVIEW}/${reviewId}`);
        } else {
          history.push(`${RoutePaths.FEATURESTORE_PENDING_FEATURE_SET}/${reviewId}`);
        }
      }
    },
    [addToast, history]
  );
  const detailsListProps: IDetailsListProps = {
    styles: isCardView ? detailsListStylesCard : detailsListStylesBasic,
    headerStyles: detailsHeaderStylesBasic,
    columns: getColumns(title, onAction, onView, isCardView, hasReviewerRole),
    isHeaderVisible: !isCardView,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      return (
        <ListRow
          styles={isCardView ? undefined : listRowStylesThin}
          hideHeader={!isCardView}
          data={item}
          columns={columns}
          loading={item.isNotLoaded}
          onMouseEnter={isCardView ? undefined : (e) => highlightRowBorder(e)}
          onMouseLeave={isCardView ? undefined : (e) => highlightRowBorder(e, false)}
        />
      );
    },
  };
  useEffect(() => {
    load();
  }, [filters]);
  const classNames = useClassNames<IPendingReviewListStyles, IPendingReviewListClassNames>(
    'PendingReviewList',
    pendingReviewListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="pending-review-list">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : isCardView ? (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="details-list-card-view">
          {data.items.length === 0 ? (
            <EmptyMessage message={`No ${title} Available`} />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      ) : (
        <div className={classNames.listView}>
          <div className={classNames.listViewWrap} data-is-scrollable="true" data-test="details-list-list-view">
            {data.items.length === 0 ? (
              <EmptyMessage message={`No ${title} Available`} />
            ) : (
              <DetailsList {...detailsListProps} />
            )}
          </div>
        </div>
      )}
      <BaseDialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        title={`${refMeta.current.actionType} Feature Set`}
        content={
          <div>
            <MessageBar messageBarType={refMeta.current.dialogInfo?.messageBarType}>
              {refMeta.current.dialogInfo?.message(refMeta.current.accessRequest)}
            </MessageBar>
            {refMeta.current.actionType !== RequestAction.Delete && (
              <div style={{ marginTop: 16 }}>
                <TextField
                  placeholder="Reason"
                  multiline
                  onChange={(_e, value) => (refMeta.current.reason = value || '')}
                />
              </div>
            )}
          </div>
        }
        footer={
          <>
            <Link styles={[linkStylesGhost, { root: { marginRight: 8 } }]} onClick={toggleHideDialog}>
              Cancel
            </Link>
            <Link
              styles={
                refMeta.current.actionType === RequestAction.Reject
                  ? [linkStylesGhost, refMeta.current.dialogInfo?.linkStyles]
                  : linkStylesPrimary
              }
              onClick={() => {
                toggleHideDialog();
                doAction();
              }}
            >{`${refMeta.current.actionType} Feature Set`}</Link>
          </>
        }
      ></BaseDialog>
    </div>
  );
};
